import { Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import html2canvas from "html2canvas";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import JSZip from "jszip";
import { saveAs } from "file-saver";

// Components
import ScopeOneBackup from "../scope1/index";
import ScopeTwo from "../scope2";
import ScopeThree from "../scope3";
import Dashboard from "..";
import { common, overview } from "../../../i18n";
import alertService from "../../../components/alert/alertService"; // 🌟 Import alertService

// 🌟 เพิ่ม Interface สำหรับรับ Props ควบคุมจากตัวแม่
interface PreviewExportProps {
  onLoading?: (isLoading: boolean) => void;
  onSuccess?: () => void;
}

export const PreviewExport = ({
  onLoading,
  onSuccess,
}: PreviewExportProps): JSX.Element => {
  const theme = useTheme();

  const { t } = useTranslation([common, overview], { nsMode: "fallback" });

  const chartRef1 = useRef<HTMLDivElement>(null);
  const chartRef2 = useRef<HTMLDivElement>(null);
  const chartRef3 = useRef<HTMLDivElement>(null);
  const chartRefSummary = useRef<HTMLDivElement>(null);

  const [isExporting, setIsExporting] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const saveChartAsImage = async () => {
    if (isExporting) return;

    setIsExporting(true);
    onLoading?.(true); // บอกแม่ว่ากำลังโหลด ห้ามปิดนะ

    try {
      const chartRefList = [
        { ref: chartRefSummary, name: "scope-summary-export.png" },
        { ref: chartRef1, name: "scope-1-export.png" },
        { ref: chartRef2, name: "scope-2-export.png" },
        { ref: chartRef3, name: "scope-3-export.png" },
      ];

      const zip = new JSZip();
      const folder = zip.folder("charts");

      for (const { ref, name } of chartRefList) {
        if (!isMounted.current) return;
        if (!ref.current) continue;

        const cloned = ref.current.cloneNode(true) as HTMLElement;

        Object.assign(cloned.style, {
          width: "1920px",
          padding: "40px",
          background: "white",
          boxSizing: "border-box",
          position: "absolute",
          left: "-9999px",
        });

        document.body.appendChild(cloned);

        try {
          await new Promise((resolve) => setTimeout(resolve, 50));
          if (!isMounted.current) return;

          const canvas = await html2canvas(cloned, { scale: 2, useCORS: true });

          const dataUrl = canvas.toDataURL("image/png");
          const imgData = dataUrl.split(",")[1];

          folder?.file(name, imgData, { base64: true });
        } finally {
          if (document.body.contains(cloned)) {
            document.body.removeChild(cloned);
          }
        }
      }

      if (!isMounted.current) return;

      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, "charts.zip");

      alertService.mixin(t("Download-Success"));

      // 🌟 สั่งปิด Modal ผ่าน Prop ที่แม่ส่งมาให้
      onSuccess?.();
    } catch (error) {
      console.error("Export charts failed:", error);
      alertService.mixin(t("Download-Failed"));
    } finally {
      if (isMounted.current) {
        setIsExporting(false);
        onLoading?.(false); // บอกแม่ว่าโหลดเสร็จแล้ว
      }
    }
  };

  return (
    <Box display={"flex"} flexDirection={"column"} height={"70dvh"} sx={{ position: "relative" }}>
      {isExporting && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            backgroundColor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "32px 48px",
              boxShadow: "0px 8px 24px rgba(69,69,80,0.15)",
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body1" fontWeight={500}>{t("Exporting")}...</Typography>
          </Box>
        </Box>
      )}

      <Box
        textAlign={"right"}
        sx={{
          color: theme.palette.primary.contrastText,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={saveChartAsImage}
          disabled={isExporting}
          sx={{ marginBottom: "24px" }}
        >
          {isExporting ? `${t("Exporting")}...` : t("Download")}
        </Button>
      </Box>

      <Box flex={1} width={"100%"} sx={{ overflow: "auto" }}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={"44px"}
          sx={{ width: "1920px", height: "auto" }}
        >
          <Box ref={chartRefSummary}>
            <Dashboard preview />
          </Box>
          <Box ref={chartRef1}>
            <ScopeOneBackup preview />
          </Box>
          <Box ref={chartRef2}>
            <ScopeTwo preview />
          </Box>
          <Box ref={chartRef3}>
            <ScopeThree preview />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
